import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Swiper, SwiperSlide } from 'swiper/react'

import icons from 'assets/icons'
import fetchStopWeather from 'backend/weather'
import useLanguage from 'hooks/useLanguage'
import useShips from 'hooks/useShips'
import Cruise from 'types/Cruise'
import Ship from 'types/Ship'
import Stopover, { Weather } from 'types/Stopover'
import { unCapitalize } from 'utils/string'
import { isShip } from 'utils/types'

import Close from './Close'
import ContentText from './ContentText'
import Image from './Image'
import NextCruiseItem from './NextCruiseItem'
import Panorama from './Panorama'
import SwiperButtons from './SwiperButtons'

import 'swiper/css'

type Props = {
  closeModal: () => void

  item: Stopover
}

const queryParams = new URLSearchParams(window.location.search)
const isRoadShow = queryParams.get('roadShow') === 'true'

const getWeatherImage = (weather: Weather) => {
  const filename = weather.img.split('/').pop()
  const iconname = filename?.split('@')[0]
  if (iconname && icons[iconname as keyof typeof icons]) {
    return icons[iconname as keyof typeof icons]
  }
  return weather.img
}

const StopoverInfo: React.FC<Props> = ({ closeModal, item }: Props) => {
  const language = useLanguage()
  const { ships } = useShips()

  const { t } = useTranslation()
  const [panorama, setPanorama] = useState<{ title: string; url: string } | null>(null)
  const [weather, setWeather] = useState<null | Weather>(null)

  console.log(weather)

  useEffect(() => {
    fetchStopWeather(item.port_id).then(setWeather)
  }, [item])

  const getShipName = useCallback(() => {
    if (isShip(item)) {
      const index = ships.findIndex((value: Ship) => value.code === item.code)
      if (index !== -1) {
        return unCapitalize(ships[index].name)
      }
    }
  }, [item, ships])

  const closePanorama = useCallback(() => {
    setPanorama(null)
  }, [])

  return (
    <div className='panoramaContainer'>
      <Panorama panorama={panorama} closePanorama={closePanorama} />
      <div className={item === null ? 'pano--unshown' : 'pano'}>
        <div className='modal--closeDesktop' onClick={closeModal}>
          <Close />
        </div>
        <div className='modal--scroll'>
          <div style={{ padding: '24px' }}>
            <div className='modal--close' onClick={closeModal}>
              <img src={icons.cross} alt='close' width={15} height={15} />
            </div>
            <div className='modal--container'>
              <div className='modal--header modal--header--stopHeight'>
                {weather && (
                  <div className='modal--weather'>
                    <div className={`modal--weather--image modal--weather--${weather.daynight}`}>
                      <img
                        className='modal--weather--img'
                        alt='weather'
                        src={getWeatherImage(weather)}
                      />
                    </div>
                    <div className='text text--size13 text--weight600'>{weather.temperature}</div>
                  </div>
                )}
                <Image
                  className='modal--img'
                  loadingClassName='modal--img--loading'
                  src={item.port_images[language]}
                  alt='img'
                />
              </div>
              <div className='modal--title'>
                <p className='text text--size48 modal--title--name text--center text--nomargin'>
                  {unCapitalize(item.port_name[language])}
                </p>
              </div>
              <div className='modal--content--container'>
                <ContentText content={item.port_description[language]} fullScreen={false} />
              </div>
            </div>

            {item.poi && item.poi.length > 0 && (
              <React.Fragment>
                <Swiper
                  slidesPerView={'auto'}
                  spaceBetween={13}
                  className='modal--nextCruise surroundings'
                  onSwiper={(swiper) => {
                    // Type assertion to 'any' to bypass the type checking error
                    ;(swiper as any).snapGrid = [...(swiper as any).slidesGrid]
                  }}
                  onReachEnd={(swiper) => {
                    ;(swiper as any).snapGrid = [...(swiper as any).slidesGrid]
                  }}
                >
                  <div className='modal--nextCruise--header surroundings--header'>
                    <p className='text text--size24 text--nomargin'>{t('stopover.panoramas')}</p>
                    {item.poi.length > 2 && <SwiperButtons />}
                  </div>
                  {item.poi.map((value: any) => {
                    return (
                      <SwiperSlide key={value.poi_name[language]}>
                        <div className='modal--panorama--item' onClick={() => setPanorama(value)}>
                          <div className='modal--panorama--image--container'>
                            <img
                              src={value.poi_visuel_360}
                              alt={value.title}
                              className='modal--panorama--image'
                            />
                            <div className='modal--panorama--layer'>
                              <img
                                className='modal--actions--item--image'
                                src={icons.round}
                                alt='360'
                              />
                            </div>
                          </div>
                          <p className='text modal--panorama--text text--lineHeight24'>
                            {value.poi_name[language]}
                          </p>
                        </div>
                      </SwiperSlide>
                    )
                  })}
                </Swiper>
              </React.Fragment>
            )}

            {item.next_cruise && item.next_cruise.length > 0 && (
              <div className='modal--cruise--div'>
                {item.next_cruise.length > 1 ? (
                  <Swiper
                    slidesPerView={'auto'}
                    spaceBetween={20}
                    className='modal--nextCruise'
                    onSwiper={(swiper) => {
                      // Type assertion to 'any' to bypass the type checking error
                      ;(swiper as any).snapGrid = [...(swiper as any).slidesGrid]
                    }}
                    onReachEnd={(swiper) => {
                      ;(swiper as any).snapGrid = [...(swiper as any).slidesGrid]
                    }}
                  >
                    <div className='modal--nextCruise--header'>
                      <p className='text text--size24 text--nomargin'>
                        {t('stopover.nextCruiseMulti')}
                      </p>
                      {item.next_cruise.length > 1 && <SwiperButtons />}
                    </div>
                    {item.next_cruise.map((cruise: Cruise) => (
                      <SwiperSlide key={cruise.cruise_id}>
                        <NextCruiseItem
                          cruise={cruise}
                          shipName={getShipName()}
                          isRoadShow={isRoadShow}
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                ) : (
                  <div className='modal--cruise--div modal--cruise--single'>
                    <div style={{ width: '100%' }}>
                      <div className='modal--nextCruise--header'>
                        <p className='text text--size24 text--nomargin'>
                          {t('stopover.nextCruise')}
                        </p>
                      </div>
                      <NextCruiseItem
                        cruise={item.next_cruise[0]}
                        shipName={getShipName()}
                        isRoadShow={isRoadShow}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {item.next_cruise && item.next_cruise.length > 0 && !isRoadShow && (
              <div className='modal--cruise--all'>
                <a
                  className='text text--size14 text--white button--dark button--linkoutside text--center button'
                  href={item.cruise_others_url}
                  target='_blank'
                  rel='noreferrer'
                >
                  {t('stopover.allCruises')}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default StopoverInfo
